import { brands as compiledBrands } from 'virtual:today-brands'
import { type BrandRuntimeArtifact, DEFAULT_PLATFORM_BRAND_ID } from '@today/brand-config/runtime'
import { BrandProvider } from '@today/ui-primitives'
import { type ReactNode, useContext, useMemo } from 'react'
import { AuthContext } from '@/auth/AuthProvider'
import { selectViewerBrand } from './select-viewer-brand'
import { usePlatformBrandId } from './use-platform-brand'

// Fail at module load when the bundle lacks the built-in default platform brand: every
// pointer fallback ends there, so `selectViewerBrand` can never throw at runtime (TO-541 D6).
selectViewerBrand(null, compiledBrands, DEFAULT_PLATFORM_BRAND_ID)

interface BrandRootProps {
	children?: ReactNode
	brands: readonly BrandRuntimeArtifact[]
}

/** Binds the viewer's brand against an already-resolved platform brand id. */
function ViewerBrandRoot({ children, brands, platformBrandId }: BrandRootProps & { platformBrandId: string }) {
	const user = useContext(AuthContext)?.user ?? null
	const role = user?.role
	const brandId = user?.brandId
	const brand = useMemo(
		() => selectViewerBrand(role === undefined ? null : { role, brandId }, brands, platformBrandId),
		[role, brandId, brands, platformBrandId],
	)
	return (
		<BrandProvider brand={brand} applyToDocument>
			{children}
		</BrandProvider>
	)
}

/** Resolves the platform brand from the runtime pointer (TO-541 REQ-102), then binds the viewer's brand. */
function PointerBrandRoot({ children, brands }: BrandRootProps) {
	return (
		<ViewerBrandRoot brands={brands} platformBrandId={usePlatformBrandId(brands)}>
			{children}
		</ViewerBrandRoot>
	)
}

/**
 * Binds the viewer's brand around the application (backoffice-brand-binding, ADR-0045 Amendments
 * TO-482 and TO-541): the tenant's brand for a tenant-scoped viewer, the platform brand for a
 * `SUPER_ADMIN`, a signed-out visitor or an unusable brand id. The platform brand is the runtime
 * pointer (`GET /platform/brand`, else the last-known id, else `aurora`); a changed pointer or
 * viewer re-binds the same `BrandProvider` in place. Bound to `<html>` so portalled overlays are
 * themed too.
 *
 * Reads the auth context directly (not `useAuth`) so that outside an `AuthProvider` it falls back
 * to the platform brand instead of throwing. Needs a `QueryClientProvider` unless `platformBrandId`
 * is injected.
 */
export function AppBrandRoot({
	children,
	brands = compiledBrands,
	platformBrandId,
}: {
	children?: ReactNode
	/** Test seam: the compiled brand set (default `virtual:today-brands`). */
	brands?: readonly BrandRuntimeArtifact[]
	/** Test seam: a fixed platform brand id instead of the runtime pointer. */
	platformBrandId?: string
}) {
	return platformBrandId === undefined ? (
		<PointerBrandRoot brands={brands}>{children}</PointerBrandRoot>
	) : (
		<ViewerBrandRoot brands={brands} platformBrandId={platformBrandId}>
			{children}
		</ViewerBrandRoot>
	)
}
